import jsPDF from "jspdf";
import autoTable, { type RowInput } from "jspdf-autotable";

type OutstandingInvoicePdfRow = {
	invoiceNumber: string;
	customerName: string;
	mobileNumber: string;
	totalAmount: number;
	amountPaid: number;
	balance: number;
};

const formatAmount = (value: number) => `${value.toFixed(2)}`;

const buildOutstandingInvoicesExportFileName = (date: string) =>
	`outstanding-invoices-report_${date}.pdf`;

export const exportOutstandingInvoicesToPdf = (
	invoices: OutstandingInvoicePdfRow[]
) => {
	const doc = new jsPDF({
		orientation: "landscape",
		unit: "pt",
		format: "a4"
	});

	doc.setFont("helvetica", "bold");
	doc.setFontSize(16);
	doc.text("Outstanding Invoices Report", 40, 40);

	doc.setFont("helvetica", "normal");
	doc.setFontSize(10);
	doc.text(`Date: ${new Date().toLocaleDateString("en-CA")}`, 40, 58);

	const body: RowInput[] = invoices.map((invoice) => [
		invoice.invoiceNumber,
		invoice.customerName,
		invoice.mobileNumber,
		formatAmount(invoice.totalAmount),
		formatAmount(invoice.amountPaid),
		formatAmount(invoice.balance)
	]);

	const totalBalance = invoices.reduce(
		(sum, invoice) => sum + invoice.balance,
		0
	);

	autoTable(doc, {
		startY: 78,
		head: [
			[
				"Invoice Number",
				"Customer Name",
				"Mobile Number",
				"Total Amount (INR)",
				"Amount Paid (INR)",
				"Balance (INR)"
			]
		],
		body,
		theme: "grid",
		styles: {
			font: "helvetica",
			fontSize: 8,
			cellPadding: 4,
			overflow: "linebreak",
			lineColor: [220, 220, 220],
			lineWidth: 0.5
		},
		headStyles: {
			fillColor: [242, 242, 242],
			textColor: [20, 20, 20],
			fontStyle: "bold"
		},
		columnStyles: {
			2: { textColor: [0, 0, 255] },
			3: { halign: "right" },
			4: { halign: "right" },
			5: { halign: "right" }
		},
		didDrawCell: (data) => {
			if (data.section === "body" && data.column.index === 2) {
				const phone = String(data.cell.text?.[0] ?? "").trim();
				if (phone) {
					doc.link(
						data.cell.x,
						data.cell.y,
						data.cell.width,
						data.cell.height,
						{ url: `tel:${phone}` }
					);
				}
			}
		},
		foot: [
			[
				{
					content: "Total Outstanding",
					colSpan: 5,
					styles: { halign: "right", fontStyle: "bold" }
				},
				formatAmount(totalBalance)
			]
		],
		showFoot: "lastPage",
		footStyles: {
			fillColor: [242, 242, 242],
			textColor: [20, 20, 20],
			fontStyle: "bold"
		},
		margin: { top: 40, right: 40, bottom: 40, left: 40 }
	});

	const pageCount = doc.getNumberOfPages();
	for (let page = 1; page <= pageCount; page += 1) {
		doc.setPage(page);
		doc.setFont("helvetica", "normal");
		doc.setFontSize(8);
		doc.text(
			`Page ${page} of ${pageCount}`,
			doc.internal.pageSize.getWidth() - 40,
			doc.internal.pageSize.getHeight() - 20,
			{ align: "right" }
		);
	}

	doc.save(
		buildOutstandingInvoicesExportFileName(
			new Date().toISOString().split("T")[0]
		)
	);
};
